import { useEffect, useState } from 'react';
import { personnelApi } from './personnelApi';

/**
 * How many people work in each organizational unit, for the chart's per-node badge.
 *
 * **It reads the personnel list's own `unit` facet rather than a new endpoint.** That facet
 * is already a catalog feed — every active unit, with the number of people whose current,
 * primary line of service is a post in it — so the count the badge shows and the rows the
 * Employees modal lists are the *same query*, and they cannot drift apart. One request
 * returns every unit's count; asking per node would be a request per kebab.
 *
 * Two consequences worth knowing:
 *
 * - **It is gated by the endpoint, not by the caller.** `/api/personnel` carries
 *   `personnel.read`, so a reader without that key gets no counts — which is the same key the
 *   menu entry is gated on. Hiding the badge in the UI would not have been enough: a count of
 *   another module's records must not reach a caller who may not read them, and the way to
 *   guarantee that is to not make the request.
 * - **`status: 'active'` matches what the modal asks for.** The facet counts are computed with
 *   the status filter applied, so passing anything else here would badge one number and list
 *   another.
 */
export interface UnitHeadcounts {
  /**
   * The unit's headcount. `undefined` while the request is in flight or when the caller may
   * not read personnel — which `<CountBadge>` renders as nothing, where a `0` renders as a
   * visible zero. Collapsing the two would make a request in flight look like an empty unit.
   */
  countOf: (orgUnitId: string) => number | undefined;
}

export function useUnitHeadcounts(enabled: boolean): UnitHeadcounts {
  const [counts, setCounts] = useState<Map<string, number>>(new Map());

  useEffect(() => {
    if (!enabled) {
      setCounts(new Map());
      return;
    }

    const controller = new AbortController();

    /*
      One row asked for, because the rows are not what this is after — the facets travel with
      every page of the list contract, and the smallest page is the cheapest way to them.
    */
    personnelApi
      .list({ page: 1, pageSize: 1, status: 'active' }, controller.signal)
      .then((result) => {
        if (controller.signal.aborted) return;

        setCounts(
          new Map((result.facets.unit ?? []).map((facet) => [facet.value, facet.count])),
        );
      })
      .catch(() => {
        if (controller.signal.aborted) return;

        // Cleared, not left stale: no badge at all beats a headcount from the previous answer
        // presented as current.
        setCounts(new Map());
      });

    return () => controller.abort();
  }, [enabled]);

  return { countOf: (orgUnitId) => counts.get(orgUnitId) };
}
